"use client";

import { buttonVariants } from "@/components/ui/button";
import { isFeatureFlagBenefitNotificationEnable } from "@/env/config";
import { cn } from "@/lib/utils";
import type {
  AdminPermissionMap,
  Permission,
} from "@/server/actions/admin/permission";
import { pagesPath } from "@/utils/pages-path";
import Link from "next/link";

type MenuItem = {
  permission: Permission;
  label: string;
  href: string;
};

const MENU_CATEGORIES: Record<string, MenuItem[]> = {
  // ユーザー管理カテゴリのメニュー
  userManagement: [
    {
      permission: "manageMembers",
      label: "加入者管理",
      href: pagesPath.admin.list.members.$url().path,
    },
    {
      permission: "manageOffices",
      label: "事業所管理",
      href: pagesPath.admin.list.offices.$url().path,
    },
    {
      permission: "manageStaffs",
      label: "健保管理",
      href: pagesPath.admin.list.admins.$url().path,
    },
    {
      permission: "manageAccounts",
      label: "アカウント管理",
      href: pagesPath.admin.list.accounts.$url().path,
    },
    {
      permission: "listOfficeBankAccounts",
      label: "口座情報管理",
      href: pagesPath.admin.list.bank_accounts.offices.$url().path,
    },
  ],

  // 健康診断カテゴリのメニュー
  healthCheckup: [
    {
      permission: "manageHealthCheckupsFacilities",
      label: "健診マスタ",
      href: pagesPath.admin.list.medical_checkups.reference_data.facilities.$url()
        .path,
    },
    {
      permission: "manageMedicalCheckups",
      label: "健診予約",
      href: pagesPath.admin.list.medical_checkups.$url().path,
    },
    {
      permission: "manageMedicalCheckups",
      label: "健診結果・報告",
      href: pagesPath.admin.list.medical_checkups.results.$url().path,
    },
    {
      permission: "manageMedicalCheckups",
      label: "特定保健指導",
      href: pagesPath.admin.list.medical_checkups.tokutei_hokenshido.$url()
        .path,
    },
  ],

  // 通知・システムカテゴリのメニュー
  notificationSystem: [
    {
      permission: "manageEmail",
      label: "Eメール管理",
      href: pagesPath.admin.list.emails.$url().path,
    },
    {
      permission: "manageNotification",
      label: "通知管理",
      href: pagesPath.admin.list.notifications.$url().path,
    },
    {
      permission: "manageCharacterConversionRules",
      label: "加入者の認証時のデジタル認証アプリの文字変換ルール管理",
      href: pagesPath.admin.list.character_conversion_rules.$url().path,
    },
  ],

  // 申請・通知書
  applicationAndNotificationPapers: [
    {
      permission: "listApplications",
      label: "申請管理",
      href: pagesPath.admin.list.applications.$url().path,
    },
    ...(isFeatureFlagBenefitNotificationEnable()
      ? [
          {
            permission: "listNotificationPapers",
            label: "通知書管理",
            href: pagesPath.admin.list.notification_papers.$url().path,
          } satisfies MenuItem,
        ]
      : []),
  ],
};

// カテゴリ別にメニューを描画するコンポーネント
const MenuCategory = ({
  title,
  menus,
  permissions,
}: {
  title: string;
  menus: MenuItem[];
  permissions: AdminPermissionMap;
}) => {
  const visibleMenus = menus.filter((menu) => permissions[menu.permission]);

  if (visibleMenus.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 className="mb-4 border-b border-monotone-3 pb-2 text-lg font-semibold text-monotone-9">
        {title}
      </h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {visibleMenus.map((menu) => (
          <Link
            key={menu.label}
            className={cn(
              buttonVariants({ variant: "primary", size: "lg" }),
              "h-16 w-full",
            )}
            href={menu.href}
          >
            {menu.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export const AdminRootPage = ({
  userInfo,
}: {
  userInfo: {
    permissions: AdminPermissionMap;
  };
}) => {
  return (
    <div className="p-10">
      <div className="space-y-8">
        <MenuCategory
          title="ユーザー管理"
          menus={MENU_CATEGORIES.userManagement}
          permissions={userInfo.permissions}
        />
        <MenuCategory
          title="健康診断"
          menus={MENU_CATEGORIES.healthCheckup}
          permissions={userInfo.permissions}
        />
        <MenuCategory
          title="申請・通知書"
          menus={MENU_CATEGORIES.applicationAndNotificationPapers}
          permissions={userInfo.permissions}
        />
        <MenuCategory
          title="通知・システム"
          menus={MENU_CATEGORIES.notificationSystem}
          permissions={userInfo.permissions}
        />
      </div>
    </div>
  );
};
